import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import api from '../services/api';

const TEAM_MEMBERS = [
  'AbuZaid', 'Shabaz', 'Saad', 'Navneet', 'Ahtesham', 
  'Nadeem', 'Sohel', 'Jaffar', 'Zaid nagori'
];

const BUG_STATUS_OPTIONS = [
  'New and Created', 'Not a Bug', 'In progress', 'Resolved', 
  'Postponed', 'Duplicate', 'Reopened', 'Deployed and Closed', 
  'Explain', 'Pending'
];

const BUG_TYPE_OPTIONS = [
  'New Request', 'Change Request', 'Bug', 'Blocker'
];

const PRIORITY_OPTIONS = [1, 2, 3, 4, 5];

const BugForm = ({ bug, sheetId, onSubmit, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  
  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      errorDetails: bug?.errorDetails || '',
      bugFoundInVersion: bug?.bugFoundInVersion || '',
      raisedBy: bug?.raisedBy || '',
      assignedTo: bug?.assignedTo || '',
      bugStatus: bug?.bugStatus || 'New and Created',
      priority: bug?.priority || 3,
      type: bug?.type || 'Bug',
      reportToDeveloper: bug?.reportToDeveloper || '',
      teamResponse: bug?.teamResponse || '',
      bugFixedIn: bug?.bugFixedIn || '',
      complaintStatus: bug?.complaintStatus || 'New and Created',
      commentsByDev: bug?.commentsByDev || '',
      expectedResult: bug?.expectedResult || '',
      actualResult: bug?.actualResult || '',
    }
  });

  useEffect(() => {
    if (bug) {
      reset({
        errorDetails: bug.errorDetails || '',
        bugFoundInVersion: bug.bugFoundInVersion || '',
        raisedBy: bug.raisedBy || '',
        assignedTo: bug.assignedTo || '',
        bugStatus: bug.bugStatus || 'New and Created',
        priority: bug.priority || 3,
        type: bug.type || 'Bug',
        reportToDeveloper: bug.reportToDeveloper || '',
        teamResponse: bug.teamResponse || '',
        bugFixedIn: bug.bugFixedIn || '',
        complaintStatus: bug.complaintStatus || 'New and Created',
        commentsByDev: bug.commentsByDev || '',
        expectedResult: bug.expectedResult || '',
        actualResult: bug.actualResult || '',
      });
    }
  }, [bug, reset]);

  const onFormSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      
      // Add all form fields
      Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
      });
      
      // Add sheet ID
      formData.append('sheetId', sheetId);
      
      // Add image if selected
      if (imageFile) {
        formData.append('errorImage', imageFile);
      }

      if (bug) {
        await api.put(`/bugs/${bug._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.post('/bugs', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      onSubmit();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save bug');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('Image size must be less than 10MB');
        return;
      }
      setImageFile(file);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="errorDetails"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Error Details"
                fullWidth
                multiline
                rows={3}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="bugFoundInVersion"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Bug Found in Version"
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="raisedBy"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Raised By"
                fullWidth
              >
                <MenuItem value="">Select Member</MenuItem>
                {TEAM_MEMBERS.map((member) => (
                  <MenuItem key={member} value={member}>
                    {member}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="assignedTo"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Assigned To"
                fullWidth
              >
                <MenuItem value="">Select Member</MenuItem>
                {TEAM_MEMBERS.map((member) => (
                  <MenuItem key={member} value={member}>
                    {member}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="bugStatus"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Bug Status"
                fullWidth
              >
                {BUG_STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Priority (1-5, 5 is highest)"
                fullWidth
              >
                {PRIORITY_OPTIONS.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Type"
                fullWidth
              >
                {BUG_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="reportToDeveloper"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Report to Developer"
                fullWidth
              >
                <MenuItem value="">Select Developer</MenuItem>
                {TEAM_MEMBERS.map((member) => (
                  <MenuItem key={member} value={member}>
                    {member}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Error Image (Optional, max 10MB)
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            style={{ marginBottom: '16px' }}
          />
          {bug?.errorImage && (
            <Typography variant="caption" display="block">
              Current image: {bug.errorImage}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="teamResponse"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Team Response"
                fullWidth
                multiline
                rows={2}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="bugFixedIn"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Bug Fixed In"
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="complaintStatus"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Complaint Status"
                fullWidth
              >
                {BUG_STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="commentsByDev"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Comments by Dev"
                fullWidth
                multiline
                rows={2}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="expectedResult"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Expected Result"
                fullWidth
                multiline
                rows={2}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="actualResult"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Actual Result"
                fullWidth
                multiline
                rows={2}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : bug ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BugForm;